import {
  IOfferingRes,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuoteRespSchema,
  OfferingTypesEnum,
  RateBillingFrequencyEnum,
} from '@monetize/types/app';
import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { endOfDay } from 'date-fns/endOfDay';
import { parseISO } from 'date-fns/parseISO';
import { startOfDay } from 'date-fns/startOfDay';
import { useEffect, useMemo, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  DatePicker,
  DatePickerProps,
} from '../../../../../components/Monetize/DatePicker/DatePicker';
import { hasUsageProducts } from './quoteOfferingUtils';

interface QuoteOfferingScheduledEndDateProps {
  isOfferingLoading: boolean;
  quote: IQuoteRespSchema;
  control: Control<IQuoteOfferingReqSchema>;
  priorScheduledQuoteOffering?: IQuoteOfferingRespSchema | null;
  nextScheduledQuoteOffering?: IQuoteOfferingRespSchema | null;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  isChildOffering?: boolean;
  onChange?: (date: string) => void;
  offering: IOfferingRes;
  monthInterval: number;
  parentQuoteOffering?: IQuoteOfferingRespSchema | null;
  isUsingDelayedBilling?: boolean;
}

export const QuoteOfferingScheduledEndDate = ({
  isOfferingLoading,
  control,
  quote,
  priorScheduledQuoteOffering,
  nextScheduledQuoteOffering,
  isReadOnly,
  isDisabled = false,
  isChildOffering = false,
  onChange: onChangeProp,
  offering,
  monthInterval,
  parentQuoteOffering,
  isUsingDelayedBilling,
}: QuoteOfferingScheduledEndDateProps) => {
  const [maxDate, setMaxDate] = useState<Date>();
  const isMinCommit = offering.type === OfferingTypesEnum.MIN_COMMIT;

  const hasUsageProduct = hasUsageProducts(parentQuoteOffering, offering);

  // Extract stable primitive values from objects for useMemo dependencies
  const parentBillingFrequency = parentQuoteOffering?.billingFrequency;
  const parentStartDate = parentQuoteOffering?.startDate;
  const priorEndDate = priorScheduledQuoteOffering?.endDate;
  const nextEndDate = nextScheduledQuoteOffering?.endDate;
  const { contractStartDate, contractEndDate } = quote;
  const billingAnchorDay = quote?.billingAnchor?.day;

  const datepickerOptions: Partial<DatePickerProps> = useMemo(() => {
    const options: Partial<DatePickerProps> = {};

    // Restrict mode based on use-case
    if (isMinCommit) {
      options.mode =
        parentBillingFrequency === RateBillingFrequencyEnum.ANNUALLY
          ? 'YEAR'
          : 'MONTH';
      options.interval =
        parentBillingFrequency === RateBillingFrequencyEnum.ANNUALLY
          ? 1
          : (monthInterval as 1 | 3 | 6); // This will be only 1 or 3 or 6
    } else if (hasUsageProduct) {
      options.mode = 'MONTH';
      options.interval = 1;
    }

    if (isUsingDelayedBilling) {
      options.mode = 'YEAR';
      options.interval = 1;
    }

    if (isMinCommit || hasUsageProduct || isUsingDelayedBilling) {
      // Last frequency period cannot be selected
      options.minDate = priorEndDate
        ? addMonths(parseISO(priorEndDate), monthInterval)
        : addDays(addMonths(parseISO(contractStartDate), monthInterval), -1);
      options.maxDate = parseISO(nextEndDate || contractEndDate);

      if (billingAnchorDay && maxDate && (hasUsageProduct || isMinCommit)) {
        options.baseDate = new Date(
          maxDate.getFullYear(),
          maxDate.getMonth(),
          billingAnchorDay - 1, // Adjusting to the previous day
        );
      } else {
        options.baseDate = parseISO(contractEndDate);
      }

      options.anchorMonth = parseISO(contractEndDate).getMonth() + 1;
    } else {
      const minDateStr = parentStartDate || contractStartDate;
      let maxDateCalc = parseISO(contractEndDate);

      if (nextEndDate) {
        maxDateCalc = addDays(parseISO(nextEndDate), -1);
      }

      options.minDate = isChildOffering
        ? addDays(parseISO(minDateStr), 1)
        : parseISO(minDateStr);
      options.maxDate = maxDateCalc;
    }

    // Validate bounds: minDate should not be after maxDate
    if (
      options.minDate &&
      options.maxDate &&
      options.minDate.getTime() > options.maxDate.getTime()
    ) {
      options.minDate = startOfDay(options.maxDate);
      options.maxDate = endOfDay(options.minDate);
    }

    return options;
  }, [
    isMinCommit,
    hasUsageProduct,
    isUsingDelayedBilling,
    parentBillingFrequency,
    parentStartDate,
    priorEndDate,
    nextEndDate,
    monthInterval,
    contractStartDate,
    contractEndDate,
    billingAnchorDay,
    maxDate,
    isChildOffering,
  ]);

  useEffect(() => {
    const maxDateStr = parentQuoteOffering?.endDate || quote.contractEndDate;
    setMaxDate(parseISO(maxDateStr));
  }, [
    quote.contractStartDate,
    quote.contractEndDate,
    parentQuoteOffering?.endDate,
  ]);

  return (
    <Controller
      control={control}
      name={isChildOffering ? 'schedule.endDate' : 'endDate'}
      render={({ field: { onChange, value, ...rest } }) => (
        <DatePicker
          {...rest}
          value={!isChildOffering && !value ? quote.contractEndDate : value}
          {...datepickerOptions}
          onChange={(date) => {
            onChange(date);
            onChangeProp && onChangeProp(date!);
          }}
          isReadOnly={isReadOnly}
          isDisabled={isOfferingLoading || isDisabled}
          variant={isReadOnly ? 'readonly' : 'outline'}
        />
      )}
    />
  );
};
